import TokenRenewalBox from '@components/token-renewal-box/token-renewal-box.component'
import { withTransaction } from '@elastic/apm-rum-react'
import {
  Campaign,
  HubCampaignsService,
  InsideNavigationParcelComponent,
  LogService,
  MenuItemNavigation,
  NavigationLayout,
  onUserChange,
  User,
  UserHelper,
  UserInDetails,
} from '@hub/shared'
import { SqLoader } from '@squidit/react-css'
import CampaignHead from '@src/components/campaign-head/campaign-head.component'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { SubscriptionLike } from 'rxjs'
import CampaignFooterMobile from './components/campaign-footer-mobile/campaign-footer-mobile.component'
import CampaignHeadMobile from './components/campaign-head-mobile/campaign-head-mobile.component'
import './details.page.scoped.scss'
import './details.page.scss'
import ApprovePage from './pages/approve/approve.page'
import DetailingPage from './pages/detailing/detailing.page'
import EngagePage from './pages/engage/engage.page'
import MaterialNoticesPage from './pages/material-notices/material-notices.page'
import OrientationContentPage from './pages/orientation-content/orientation-content.page'
import PublishPage from './pages/publish/publish.page'
import ReportPage from './pages/report/report.page'

const DetaisCampaignPageComponent = (props: any) => {
  const [tokenValid, setTokenValid] = useState(true)
  const [tokenValidComponentHeight, setTokenValidComponentHeight] = useState(0)
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const logService = useMemo(() => new LogService(), [])
  const userHelper = useMemo(() => new UserHelper(), [])
  const hubCampaignsService = useMemo(() => new HubCampaignsService(), [])
  const [height, setHeight] = useState(window?.innerHeight)
  const [width, setWidth] = useState(window?.innerWidth)
  const [user, setUser] = useState<User | null>(null)
  const [userInCampaign, setUserInCampaign] = useState<UserInDetails>(null)
  const [t, i18n] = useTranslation('campaignDetails')
  const { idCampaign, idSocial } = useParams<{ idCampaign: string; idSocial: string }>()
  const [campaign, setCampaign] = useState<Campaign>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  logService.init('Campaign Details Page')

  useEffect(() => {
    const updateHeight = () => {
      setHeight(window?.innerHeight)
    }
    const updateWidth = () => {
      setWidth(window?.innerWidth)
    }
    window.addEventListener('resize', updateHeight)
    window.addEventListener('resize', updateWidth)
    return () => {
      window.removeEventListener('resize', updateHeight)
      window.removeEventListener('resize', updateWidth)
    }
  }, [])

  useEffect(() => {
    setTokenValidComponentHeight(tokenValid ? 0 : 162)
  }, [tokenValid])

  const homeNavigationPath = `/campaigns/${idCampaign}/details/${idSocial}`

  const [menu, setMenu] = useState<Array<MenuItemNavigation>>([
    {
      icon: 'compass',
      label: t('orientation'),
      path: `/campaigns/${idCampaign}/details/${idSocial}/orientation`,
    },
    {
      icon: 'circle-info',
      label: t('detailing'),
      path: `/campaigns/${idCampaign}/details/${idSocial}/detailing`,
    },
    {
      icon: 'flag',
      label: t('materials'),
      path: `/campaigns/${idCampaign}/details/${idSocial}/materials`,
    },
    {
      icon: 'box-check',
      label: t('approve'),
      path: `/campaigns/${idCampaign}/details/${idSocial}/approve`,
    },
    {
      icon: 'image-polaroid',
      label: t('publish'),
      path: `/campaigns/${idCampaign}/details/${idSocial}/publish`,
    },
    {
      icon: 'medal',
      label: t('engage'),
      path: `/campaigns/${idCampaign}/details/${idSocial}/engage`,
    },
    {
      icon: 'chart-line',
      label: t('analytics'),
      path: `/campaigns/${idCampaign}/details/${idSocial}/analytics`,
    },
  ])

  const parseLabelKey = (menuItem) => {
    if (menuItem?.path?.includes('detailing')) {
      return 'detailing'
    }
    if (menuItem?.path?.includes('materials')) {
      return 'materials'
    }
    if (menuItem?.path?.includes('orientation')) {
      return 'orientation'
    }
    if (menuItem?.path?.includes('approve')) {
      return 'approve'
    }
    if (menuItem?.path?.includes('publish')) {
      return 'publish'
    }
    if (menuItem?.path?.includes('engage')) {
      return 'engage'
    }
    if (menuItem?.path?.includes('analytics')) {
      return 'analytics'
    }
    return ''
  }

  const toSummary = useCallback(() => {
    navigate(`/campaigns/${idCampaign}/summary`)
  }, [idCampaign, navigate])

  useEffect(() => {
    userHelper.getUser().then(async (newUser) => {
      if (newUser?.squidId) {
        setUser(newUser)
        if (!idSocial) {
          return toSummary()
        }
        const foundedSocialId = newUser?.identities?.find((i) => i.user_id === idSocial)
        if (!foundedSocialId) {
          return toSummary()
        }
        const response = await hubCampaignsService.getCampaignDetail(idCampaign, idSocial)
        if (!response?.campaign) {
          return history.pushState({}, '', '/error')
        }
        const userInCampaign: UserInDetails = response?.users?.find((u) => u?.idNetwork === idSocial)
        if (userInCampaign?.status !== 'recruited' || !userInCampaign) {
          return toSummary()
        }
        setUserInCampaign(userInCampaign)
        setCampaign(response?.campaign)
        setLoading(false)
      }
    })
    onUserChangeSubscription.current = onUserChange.subscribe(async (userChanged: User) => {
      setUser(userChanged)
    })
    return () => {
      onUserChangeSubscription.current?.unsubscribe()
    }
  }, [onUserChangeSubscription, idSocial, idCampaign, toSummary, hubCampaignsService, userHelper])

  useEffect(() => {
    setMenu((m) =>
      m?.map((item) => {
        return {
          ...item,
          label: t(parseLabelKey(item)),
        }
      })
    )
  }, [i18n.language, t])

  const filterMenu = (m) => {
    return m?.filter((m) => {
      if (m.path.includes('engage')) {
        return campaign?.activation?.hasGamification
      }
      if (m.path.includes('approve')) {
        return campaign?.planning?.hasContentApproval
      }
      if (m.path.includes('analytics')) {
        return campaign?.socialNetworks?.includes('instagram')
      }
      return true
    })
  }

  const findTitle = (path: string) => {
    return menu.find((item) => item.path.endsWith(path.split('/')[5]))?.label
  }

  const ToHome = () => {
    const queryString = searchParams
    return window?.innerWidth > 991 ? (
      <Navigate to={`/campaigns/${idCampaign}/details/${idSocial}/orientation?${queryString.toString()}`} />
    ) : null
  }

  const backButtonFunc = useCallback(() => {
    const origin = searchParams.get('origin')
    if (origin) {
      return navigate(`/campaigns/${origin}`)
    }
    return navigate('/campaigns')
  }, [searchParams, navigate])

  if (loading) {
    return (
      <div className="details-page loader-wrapper box">
        <SqLoader size="big" />
      </div>
    )
  }

  return (
    <NavigationLayout
      isHome={location.pathname === homeNavigationPath}
      menu={filterMenu(menu)}
      header={
        <div>
          <CampaignHead className="custom-head-campaign" campaign={campaign} user={userInCampaign} mode="details" />
          <TokenRenewalBox idProfile={userInCampaign?.idNetwork} socialNetwork={userInCampaign?.socialNetwork} className="mx-4" />
        </div>
      }
      backButton
      onClickBackButton={backButtonFunc}
      onReferrerFalseUrlBackButton="/campaigns"
      asidePadding={false}
    >
      <InsideNavigationParcelComponent homeNavigationPath={homeNavigationPath} title={findTitle(location.pathname)} footer={false}>
        <CampaignHeadMobile className="mobile-header" campaign={campaign} user={userInCampaign} onEmitTokenValid={setTokenValid} />
        <main
          className="scrollbar"
          id="details-page"
          style={{
            height: width < 991 ? `calc(${height}px - 51px - 46px - 27px - 1rem - ${tokenValidComponentHeight}px)` : null,
          }}
        >
          <Routes>
            <Route path={'/'} element={<ToHome />} />
            <Route path={'/orientation'} element={<OrientationContentPage campaign={campaign} userInCampaign={userInCampaign} />} />
            <Route path={'/detailing'} element={<DetailingPage {...props} campaign={campaign} userInCampaign={userInCampaign} />} />
            <Route path={'/materials'} element={<MaterialNoticesPage campaign={campaign} />} />
            <Route path={'/approve'} element={<ApprovePage campaign={campaign} userInCampaign={userInCampaign} />} />
            <Route path={'/publish'} element={<PublishPage campaign={campaign} userInCampaign={userInCampaign} />} />
            <Route path={'/engage/*'} element={<EngagePage campaign={campaign} userInCampaign={userInCampaign} user={user} />} />
            <Route path={'/analytics'} element={<ReportPage campaign={campaign} userInCampaign={userInCampaign} />} />
            <Route element={<Navigate to="/error/not-found" />} />
          </Routes>
          <CampaignFooterMobile className="mobile-footer" menu={filterMenu(menu)} />
        </main>
      </InsideNavigationParcelComponent>
    </NavigationLayout>
  )
}

export default withTransaction('DetaisCampaignPageComponent', 'component')(DetaisCampaignPageComponent)
